import './styles.scss';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Launch } from '@edx/paragon/icons';
import { Button, CardGrid, Container } from '@edx/paragon';
import { injectIntl, FormattedMessage, defineMessages } from 'gatsby-plugin-react-intl';

import { emitEventIfAble } from '@utils/analytics';
import { isArchived, FREE, UPGRADABLE } from '@utils/course';
import withUser from '@utils/withUser';

import courseRunShape from '@prospectus/common/types/courseRunShape';
import ProspectusLink from '@prospectus/common/ui-strict/ProspectusLink';
import { intlShape } from '@prospectus/common/types/intlShape';
import EmailOptIn from '@prospectus/common/ui/EmailOptIn';

import hideEmailOptInForCourse from './constants';
import EnrollButton from '../EnrollButton';
import SelectionTile from './components/SelectionTile';

const messages = defineMessages({
  'prospectus.course.runSelector.archivedLinkIconSrText': {
    id: 'prospectus.course.runSelector.archivedLinkIconSrText',
    description: 'Screen reader text for the launch icon on the archived link',
    defaultMessage: 'Opens in a new tab',
  },
  'prospectus.course.runSelector.archivedLinkIconTitleText': {
    id: 'prospectus.course.runSelector.archivedLinkIconTitleText',
    description: 'Title attribute text for the launch icon on the archived link',
    defaultMessage: 'Opens in a new tab',
  },
  'prospectus.course.courseRunSelector.labelMultiple': {
    id: 'prospectus.course.courseRunSelector.labelMultiple',
    description: 'Instructions to pick from a selection of course run tiles.',
    defaultMessage: 'Choose your session:',
  },
  'prospectus.course.courseRunSelector.labelSingle': {
    id: 'prospectus.course.courseRunSelector.labelSingle',
    description: 'Informing the user there is only one course session available',
    defaultMessage: 'There is one session available:',
  },
  'prospectus.course.courseRunSelector.moreDates': {
    id: 'prospectus.course.courseRunSelector.moreDates',
    description: 'This is the text on a button that, when clicked, reveals any more course runs and their dates.',
    defaultMessage: 'More Dates',
  },
  'prospectus.course.courseRunSelector.fewerDates': {
    id: 'prospectus.course.courseRunSelector.fewerDates',
    defaultMessage: 'Fewer Dates',
    description: 'This is the text on a button that, when clicked, hides extra course runs and their dates.',
  },
});

const MAX_DISPLAYED_COURSES = 4;
const MIN_ENROLLMENT_COUNT_FOR_DISPLAY = 5000;

export function BaseCourseRunSelector({
  uuid,
  owners,
  courseType,
  activeCourseRun,
  activeCourseRuns,
  enrollmentCount,
  title,
  emailOptIn,
  setEmailOptIn,
  userEnrollments,
  intl,
  mobile,
  paymentOption,
  subjects,
}) {
  const multipleRunsAvailable = activeCourseRuns.length > 1;
  const hasHiddenRuns = activeCourseRuns.length > 3;
  const [hideExtraRuns, setHideExtraRuns] = useState(true);
  const isUserEnrolled = () => {
    const keys = activeCourseRuns.map(({ key }) => key);
    return userEnrollments.some(({ courseId }) => keys.includes(courseId));
  };

  const trackEmailOptIn = (optIn) => {
    window.analytics.track('edx.bi.user.course-details.toggle-email-opt-in', {
      category: 'course-details',
      label: activeCourseRun.key,
      email_opt_in: optIn,
    });
  };

  const toggleEmailOptIn = () => {
    setEmailOptIn(!emailOptIn);
    trackEmailOptIn(!emailOptIn);
  };

  const { isEnrollable: courseIsEnrollable } = activeCourseRun;

  const getVisibleRuns = () => (hideExtraRuns ? activeCourseRuns.slice(0, MAX_DISPLAYED_COURSES) : activeCourseRuns);

  const toggleRunVisibility = () => {
    if (hideExtraRuns) {
      emitEventIfAble('edx.bi.course-about-page.more-dates');
    }
    setHideExtraRuns(!hideExtraRuns);
  };

  const hasInstructorPacedCourses = activeCourseRuns.some(({ pacingType }) => pacingType === 'instructor_paced');
  const hasSelfPacedCourses = activeCourseRuns.some(({ pacingType }) => pacingType === 'self_paced');
  const tagPacingOnTiles = hasInstructorPacedCourses && hasSelfPacedCourses;
  const userEnrolled = isUserEnrolled();

  const archivedLink = (
    <ProspectusLink
      className="archived-link"
      linkProps={{
        target: '_blank',
        rel: 'noopener noreferrer',
      }}
      href="https://support.edx.org/hc/en-us/articles/207201017-What-does-archived-mean"
      analytics={{
        eventName: 'edx.bi.user.course.archived-link.click',
      }}
    >
      <FormattedMessage
        id="prospectus.course.runSelector.archivedLink"
        defaultMessage="archived"
        description="Text on an inline link to an explanation of archived course runs. The sentence containing the link is 'After a course session ends it will be [archived]'"
      />
      <Launch
        className="ml-1 mt-n1"
        width="20"
        height="20"
        role="img"
        aria-label=""
        aria-hidden="true"
        focusable="false"
        title={intl.formatMessage(messages['prospectus.course.runSelector.archivedLinkIconTitleText'])}
      />
      <span className="sr-only">
        {intl.formatMessage(messages['prospectus.course.runSelector.archivedLinkIconSrText'])}
      </span>
    </ProspectusLink>
  );

  return (
    <Container size="lg" className="course-selection mt-md-4" id="enroll">
      {/* use old design for archived/unenrollable courses */}
      {(isArchived(activeCourseRun) || !activeCourseRun.isEnrollable) && !activeCourseRun.hidden && (
        <EnrollButton
          uuid={uuid}
          owners={owners}
          cohesionVariant={courseType}
          cohesionCategory={subjects.length > 0 ? subjects[0].name : 'not-available'}
          cohesionName={title}
          activeCourseRun={activeCourseRun}
          emailOptIn={emailOptIn}
          title={title}
        />
      )}
      {!isArchived(activeCourseRun) && activeCourseRun.isEnrollable && (
        <div>
          <h3>
            {multipleRunsAvailable
              ? intl.formatMessage(messages['prospectus.course.courseRunSelector.labelMultiple'])
              : intl.formatMessage(messages['prospectus.course.courseRunSelector.labelSingle'])}
          </h3>
          <div className="small" data-test-id="selector-subheading">
            {!mobile && enrollmentCount > MIN_ENROLLMENT_COUNT_FOR_DISPLAY && (
              <div className="small">
                <FormattedMessage
                  id="prospectus.course.runSelector.subheading"
                  defaultMessage="{numberEnrolled} already enrolled! After a course session ends, it will be {link}."
                  description="Subheading of the course run selector describing the number of users already enrolled and linking to the definition of an archived course run. Full sentence with link: 1234 already enrolled! After a course session ends, it will be [archived]."
                  values={{ numberEnrolled: enrollmentCount.toLocaleString(), link: archivedLink }}
                />
              </div>
            )}
            {mobile
              && (paymentOption === FREE || paymentOption === UPGRADABLE)
              && enrollmentCount > MIN_ENROLLMENT_COUNT_FOR_DISPLAY && (
                <div className="d-md-none x-small heading-label text-brand-500">
                  <FormattedMessage
                    id="prospectus.course.runSelector.mobile.mobileSubheading"
                    defaultMessage="{numberEnrolled} already enrolled!"
                    description="Shorter subheading of the course run selector describing the number of users already enrolled."
                    values={{ numberEnrolled: enrollmentCount.toLocaleString() }}
                  />
                </div>
            )}
            {enrollmentCount <= MIN_ENROLLMENT_COUNT_FOR_DISPLAY && (
              <FormattedMessage
                id="prospectus.course.runSelector.archivedMessage"
                defaultMessage="After a course session ends, it will be {link}."
                description="Message linking to explanation of archived course runs"
                values={{ link: archivedLink }}
              />
            )}
          </div>
          <CardGrid
            columnSizes={{
              sm: 12,
              md: 4,
              xl: 3,
            }}
            className="mt-3.5 mb-1"
          >
            {getVisibleRuns().map((run) => (
              <SelectionTile
                intl
                tracking
                courseRun={run}
                selected={run.uuid === activeCourseRun.uuid}
                multipleRunsAvailable={multipleRunsAvailable}
                isMainAdvertisedRun={run.uuid === activeCourseRuns[0].uuid}
                key={`courserun-${run.uuid}`}
                uuid={uuid}
                owners={owners}
                cohesionVariant={courseType}
                cohesionCategory={subjects.length > 0 ? subjects[0].name : 'not-available'}
                cohesionName={title}
                emailOptIn={emailOptIn}
                includePacing={tagPacingOnTiles}
                enrolledInOneRun={userEnrolled}
              />
            ))}
          </CardGrid>
        </div>
      )}
      {hasHiddenRuns && (
        <Button variant="inline" className="p-0 mb-3" onClick={toggleRunVisibility}>
          <u className="small">
            {hideExtraRuns
              ? intl.formatMessage(messages['prospectus.course.courseRunSelector.moreDates'])
              : intl.formatMessage(messages['prospectus.course.courseRunSelector.fewerDates'])}
          </u>
        </Button>
      )}
      <div className="d-flex flex-column">
        {!hideEmailOptInForCourse.includes(uuid)
          && !userEnrolled
          && !isArchived(activeCourseRun)
          && courseIsEnrollable && (
            <EmailOptIn optIn={emailOptIn} partners={owners} title={title} toggleEmailOptIn={toggleEmailOptIn} />
        )}
      </div>
    </Container>
  );
}

BaseCourseRunSelector.propTypes = {
  uuid: PropTypes.string.isRequired,
  owners: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  activeCourseRun: PropTypes.shape(courseRunShape).isRequired,
  activeCourseRuns: PropTypes.arrayOf(PropTypes.shape(courseRunShape).isRequired),
  enrollmentCount: PropTypes.number,
  title: PropTypes.string.isRequired,
  emailOptIn: PropTypes.bool,
  setEmailOptIn: PropTypes.func,
  intl: intlShape.isRequired,
  userEnrollments: PropTypes.arrayOf(
    PropTypes.shape({
      courseId: PropTypes.string.isRequired,
      mode: PropTypes.string.isRequired,
    }),
  ),
  mobile: PropTypes.bool,
  paymentOption: PropTypes.string,
  courseType: PropTypes.string,
  subjects: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
};

BaseCourseRunSelector.defaultProps = {
  activeCourseRuns: [],
  enrollmentCount: 0,
  emailOptIn: false,
  setEmailOptIn: () => {},
  userEnrollments: [],
  mobile: false,
  paymentOption: '',
  courseType: null,
  subjects: [],
};

const mapStateToProps = (store) => ({
  activeCourseRun: store.activeCourseRun,
});

const CourseRunSelector = connect(mapStateToProps, null)(withUser(injectIntl(BaseCourseRunSelector)));

export default CourseRunSelector;
